import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Building2, BookOpen, Users, UploadCloud, LogOut, TrendingUp, Package,
  Store, Percent, Plus, Copy, CheckCircle2, BarChart3, Palette, Image as ImageIcon,
  Save, ExternalLink, Shield, Gift,
} from 'lucide-react';
import { toast } from 'sonner';
import AccountSecurity from './AccountSecurity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (n) => `$${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const EditorialDashboard = () => {
  const { user, token, logout } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [tab, setTab] = useState('resumen');
  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [works, setWorks] = useState([]);
  const [authors, setAuthors] = useState([]);
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const bannerRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [brandColor, setBrandColor] = useState('#f59e0b');
  const [description, setDescription] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState('');

  const isPremium = (me?.editorial_tier || 'lite') === 'premium';

  const load = async () => {
    try {
      const [m, s, w, a] = await Promise.all([
        axios.get(`${API}/editorial/me`, { headers }),
        axios.get(`${API}/editorial/summary`, { headers }),
        axios.get(`${API}/editorial/works`, { headers }),
        axios.get(`${API}/editorial/authors`, { headers }),
      ]);
      setMe(m.data); setSummary(s.data); setWorks(w.data.works || []); setAuthors(a.data.authors || []);
      setBrandColor(m.data.brand_color || '#f59e0b');
      setDescription(m.data.editorial_description || '');
    } catch (e) {
      toast.error('Error al cargar el panel de editorial');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onUploadAsset = async (kind, f) => {
    if (!f) return;
    setUploadingAsset(kind);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(`${API}/editorial/profile/${kind}`, form, { headers });
      setMe((prev) => ({ ...prev, [`${kind}_url`]: res.data.url }));
      toast.success(kind === 'logo' ? 'Logo actualizado' : 'Banner actualizado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setUploadingAsset('');
    }
  };

  const saveBranding = async () => {
    setSavingBrand(true);
    try {
      await axios.patch(`${API}/editorial/profile`, {
        brand_color: brandColor,
        editorial_description: description,
      }, { headers });
      setMe((prev) => ({ ...prev, brand_color: brandColor, editorial_description: description }));
      toast.success('Branding guardado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSavingBrand(false);
    }
  };

  const onUpload = async (f) => {
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await axios.post(`${API}/editorial/works/upload-metadata`, form, { headers });
      const d = res.data;
      toast.success(`Metadata: ${d.created} obras creadas, ${d.skipped_existing} ya existían`);
      await load();
      setTab('obras');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo procesar el archivo');
    } finally {
      setUploading(false);
    }
  };

  const tabs = [
    { id: 'resumen', label: 'Resumen de Ventas', icon: BarChart3 },
    { id: 'beneficios', label: 'Beneficios', icon: Gift },
    { id: 'obras', label: 'Obras', icon: BookOpen },
    { id: 'autores', label: 'Mis Autores', icon: Users },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'seguridad', label: 'Seguridad', icon: Shield },
    { id: 'subir', label: 'Subir Metadata', icon: UploadCloud },
  ];

  return (
    <div className="min-h-screen bg-[#0b0b16]" data-testid="editorial-dashboard">
      {/* Header */}
      <header className="bg-[#12122099] backdrop-blur border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="gotham-bold text-white text-lg leading-none" data-testid="editorial-name">
                {me?.editorial_name || 'Editorial'}
              </div>
              <div className="text-white/50 text-xs mt-0.5">
                Editorial · <span className={isPremium ? 'text-amber-400' : 'text-white/60'}>
                  {isPremium ? 'Premium' : 'Lite'}
                </span>
              </div>
            </div>
          </div>
          <Button data-testid="editorial-logout" onClick={logout} variant="outline"
            className="border-white/20 text-white/80 hover:bg-white/10">
            <LogOut className="w-4 h-4 mr-2" /> Salir
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Ingresos totales', value: money(summary?.total_gross_income), icon: TrendingUp, c: 'from-emerald-500 to-teal-600' },
            { label: 'Unidades vendidas', value: summary?.total_units_sold ?? 0, icon: Package, c: 'from-violet-500 to-purple-600' },
            { label: 'Obras registradas', value: me?.total_works ?? 0, icon: BookOpen, c: 'from-amber-500 to-orange-600' },
            { label: 'Mis autores', value: me?.total_authors ?? 0, icon: Users, c: 'from-cyan-500 to-blue-600' },
          ].map((k, i) => (
            <Card key={i} className="bg-white/[0.03] border-white/10" data-testid={`editorial-kpi-${i}`}>
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${k.c} flex items-center justify-center`}>
                  <k.icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="gotham-bold text-white text-xl leading-none">{k.value}</div>
                  <div className="text-white/50 text-xs mt-1">{k.label}</div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Free ebooks banner */}
        {me && me.ebooks_used >= me.free_ebooks_limit && (
          <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-amber-200 text-sm" data-testid="editorial-upgrade-banner">
            Has usado {me.ebooks_used} de tus {me.free_ebooks_limit} ebooks gratuitos. Contacta a Viubux para ampliar tu paquete.
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 border-b border-white/10 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`editorial-tab-${t.id}`}
              className={`gotham-medium px-4 py-2.5 whitespace-nowrap flex items-center gap-2 transition-colors ${
                tab === t.id ? 'text-white border-b-2 border-amber-500' : 'text-white/55 hover:text-white'
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'resumen' && (
          <SalesSummary summary={summary} isPremium={isPremium} />
        )}
        {tab === 'obras' && (
          <WorksList works={works} isPremium={isPremium} headers={headers} authors={authors} onChange={load} />
        )}
        {tab === 'autores' && (
          <AuthorsList authors={authors} headers={headers} onChange={load} />
        )}
        {tab === 'seguridad' && (
          <div data-testid="editorial-seguridad-tab">
            <AccountSecurity />
          </div>
        )}
        {tab === 'branding' && (
          <div className="max-w-3xl space-y-6" data-testid="editorial-branding-tab">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="gotham-bold text-white text-lg flex items-center gap-2">
                <Palette className="w-5 h-5 text-amber-400" /> Personaliza tu página pública
              </h3>
              {me?.editorial_slug && (
                <a href={`/editorial/${me.editorial_slug}`} target="_blank" rel="noreferrer"
                  data-testid="editorial-view-public-page"
                  className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300">
                  <ExternalLink className="w-4 h-4" /> Ver página pública
                </a>
              )}
            </div>

            {/* Banner */}
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-5 space-y-3">
                <div className="text-white/80 gotham-medium text-sm flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-amber-400" /> Banner (portada)
                </div>
                <div className="rounded-xl overflow-hidden border border-white/10 bg-black/40 h-40 flex items-center justify-center"
                  style={me?.banner_url ? {} : { background: `linear-gradient(135deg, ${brandColor}33, transparent)` }}>
                  {me?.banner_url
                    ? <img src={me.banner_url} alt="banner" className="w-full h-full object-cover" data-testid="editorial-banner-preview" />
                    : <span className="text-white/40 text-sm">Sin banner</span>}
                </div>
                <input ref={bannerRef} type="file" accept="image/*" className="hidden"
                  data-testid="editorial-banner-file"
                  onChange={(e) => onUploadAsset('banner', e.target.files?.[0])} />
                <Button data-testid="editorial-banner-upload-btn" onClick={() => bannerRef.current?.click()}
                  disabled={uploadingAsset === 'banner'} variant="outline"
                  className="border-white/20 text-white/80 hover:bg-white/10">
                  <UploadCloud className="w-4 h-4 mr-2" /> {uploadingAsset === 'banner' ? 'Subiendo...' : 'Subir banner'}
                </Button>
              </CardContent>
            </Card>

            {/* Logo + Color */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="bg-white/[0.03] border-white/10">
                <CardContent className="p-5 space-y-3">
                  <div className="text-white/80 gotham-medium text-sm flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-amber-400" /> Logo
                  </div>
                  <div className="w-24 h-24 rounded-xl overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center">
                    {me?.logo_url
                      ? <img src={me.logo_url} alt="logo" className="w-full h-full object-contain" data-testid="editorial-logo-preview" />
                      : <Building2 className="w-8 h-8 text-white/30" />}
                  </div>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden"
                    data-testid="editorial-logo-file"
                    onChange={(e) => onUploadAsset('logo', e.target.files?.[0])} />
                  <Button data-testid="editorial-logo-upload-btn" onClick={() => logoRef.current?.click()}
                    disabled={uploadingAsset === 'logo'} variant="outline"
                    className="border-white/20 text-white/80 hover:bg-white/10">
                    <UploadCloud className="w-4 h-4 mr-2" /> {uploadingAsset === 'logo' ? 'Subiendo...' : 'Subir logo'}
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-white/[0.03] border-white/10">
                <CardContent className="p-5 space-y-3">
                  <div className="text-white/80 gotham-medium text-sm flex items-center gap-2">
                    <Palette className="w-4 h-4 text-amber-400" /> Color de marca
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                      data-testid="editorial-brand-color-input"
                      className="w-14 h-14 rounded-lg bg-transparent border border-white/10 cursor-pointer" />
                    <input type="text" value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                      data-testid="editorial-brand-color-text"
                      className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm w-32" />
                  </div>
                  <div className="h-8 rounded-lg" style={{ backgroundColor: brandColor }} />
                </CardContent>
              </Card>
            </div>

            {/* Descripción */}
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-5 space-y-3">
                <div className="text-white/80 gotham-medium text-sm">Descripción de tu editorial</div>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  maxLength={600} rows={4}
                  data-testid="editorial-description-input"
                  placeholder="Cuéntale al público quién eres y qué publicas..."
                  className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" />
                <div className="text-white/40 text-xs text-right">{description.length}/600</div>
              </CardContent>
            </Card>

            <Button data-testid="editorial-save-branding-btn" onClick={saveBranding} disabled={savingBrand}
              className="bg-amber-600 hover:bg-amber-500 text-white">
              <Save className="w-4 h-4 mr-2" /> {savingBrand ? 'Guardando...' : 'Guardar branding'}
            </Button>
          </div>
        )}
        {tab === 'subir' && (
          <div className="max-w-xl">
            <Card className="bg-white/[0.03] border-white/10">
              <CardContent className="p-6 space-y-4">
                <h3 className="gotham-bold text-white text-lg flex items-center gap-2">
                  <UploadCloud className="w-5 h-5 text-amber-400" /> Subir metadata de obras
                </h3>
                <p className="text-white/60 text-sm">
                  Sube un Excel (.xlsx) o CSV con columnas: <strong>ISBN</strong>, <strong>Título</strong>,
                  Autor, Sinopsis, Formato. Las obras se registran a nombre de tu editorial (Colección = {me?.editorial_name}).
                  Los ISBN ya registrados se omiten.
                </p>
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
                  data-testid="editorial-metadata-file"
                  onChange={(e) => onUpload(e.target.files?.[0])} />
                <Button data-testid="editorial-metadata-upload-btn" onClick={() => fileRef.current?.click()}
                  disabled={uploading} className="bg-amber-600 hover:bg-amber-500 text-white">
                  <UploadCloud className="w-4 h-4 mr-2" />
                  {uploading ? 'Procesando...' : 'Elegir archivo'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
        {tab === 'beneficios' && (
          <EditorialBenefits isPremium={isPremium} />
        )}
      </div>
    </div>
  );
};

const BENEFITS = [
  { n: 1, e: '🔗', t: 'Obtén tu enlace Único y Sube tu PI', d: 'Sube tu PI (hasta 2GB de Hosting para tus libros, ebooks, audiolibros, podcast, curso, conferencias, booktrailer, etc.) y forma parte del Catálogo de Suscripción de Viubux Connect y comienza a monetizar HOY mismo.' },
  { n: 2, e: '⭐', t: 'Comunidad VIP + Pool 42%', d: 'Únete a la comunidad VIP de VC y ten acceso inmediato al Pool de Autores del 42% (adicional al 25% del rapid bonus).' },
  { n: 3, e: '🤲', t: 'Hasta 50% en Co-Producciones', d: 'Obtén hasta 50% de apoyo en Co-Producciones + un 15% de Descuento en todos los servicios que Viubux Media ofrece para ti.', link: 'https://Cotizaciones.Viubux.com', cta: '💼 Conocer Más' },
  { n: 4, e: '🎧', t: 'Catálogo Viubux Connect', d: 'Acceso al catálogo de Viubux Connect en Viubux Reader y Viubux Play.', link: 'https://app.viubux.com', cta: '🎧 Ir a Viubux Reader' },
  { n: 5, e: '📺', t: 'Tu Canal en Viubux Play', d: 'Recibe soporte técnico para actualizarlo, subir tu contenido y personalizarlo.', link: 'https://Cotizaciones.Viubux.com', cta: '📺 Solicitar Soporte' },
  { n: 6, e: '💰', t: 'Descuentos Exclusivos', d: 'En contenido de Alto Valor Transformacional y servicios ofrecidos por miembros de la comunidad.' },
  { n: 7, e: '🤝', t: 'Networking Privado', d: 'Con Socios Estratégicos de la comunidad.', link: 'https://connect.viubux.com/networking', cta: 'Ir a Community Networking' },
  { n: 8, e: '🎥', t: 'LiveStream + Eventos', d: 'LiveStream + publica tus eventos ante la comunidad.' },
  { n: 9, e: '🚀', t: 'Suite Pro-Marketing', d: 'Acceso a la Suite de Herramientas tecnológicas: Pro-Marketing.' },
  { n: 10, e: '🎤', t: '1 Evento Privado al Año', d: 'Patrocinio de un Business Room o MiniForo (máx. 20 personas) con Audio y Video + grabación de tu ponencia en tu canal de Viubux Play.' },
  { n: 11, e: '📸', t: 'PhotoShoot + 3 Stories', d: 'Para dar a conocer tus obras y tu canal de Viubux Play.' },
];

const EditorialBenefits = ({ isPremium }) => (
  <div className="space-y-4" data-testid="editorial-beneficios-tab">
    <div>
      <h3 className="gotham-bold text-white text-2xl flex items-center gap-2">
        <Gift className="w-6 h-6 text-amber-400" /> Beneficios de tu Membresía Editorial
      </h3>
      <p className="text-white/60 text-sm mt-1">
        {isPremium
          ? 'Como Editorial Premium participas en el Pool de Autores del 42%, ganas el 25% de rapid bonus y accedes a todos estos beneficios exclusivos.'
          : 'Estos son los beneficios disponibles en el ecosistema Viubux Connect. Mejora a Premium para participar en el Pool 42% y el rapid bonus.'}
      </p>
    </div>
    {BENEFITS.map((b) => (
      <Card key={b.n} className="bg-white/[0.03] border-white/10" data-testid={`editorial-benefit-${b.n}`}>
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <span className="text-3xl">{b.e}</span>
            <div className="flex-1">
              <h4 className="gotham-bold text-white text-lg mb-1">{b.n}. {b.t}</h4>
              <p className="text-white/70 text-sm">{b.d}</p>
              {b.link && (
                <a href={b.link} target="_blank" rel="noopener noreferrer" className="inline-block mt-3">
                  <Button className="bg-amber-500 hover:bg-amber-600 text-white gotham-bold" size="sm">{b.cta}</Button>
                </a>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
    <Card className="bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-transparent border-amber-500/30">
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-2xl">🏆</span>
          <h4 className="gotham-bold text-white text-lg">Bonos adicionales para autores destacados</h4>
        </div>
        <ul className="space-y-2 text-white/70 text-sm">
          <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Entrevistas en medios y podcasts (ADN40, Radio Disney, etc.)</li>
          <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Participa como Speaker en la convención anual de VC del siguiente año y tu libro destacado en el stand de la FIL de Guadalajara</li>
          <li className="flex items-start gap-2"><span className="text-amber-400 mt-0.5">•</span> Programa Piloto para tu Podcast o Curso</li>
        </ul>
        <p className="text-white/45 text-xs mt-4 italic">
          * Autor destacado es aquel que genera al menos 20 suscriptores en su canal de Viubux Connect durante 3 meses consecutivos.
        </p>
      </CardContent>
    </Card>
  </div>
);

const SalesSummary = ({ summary, isPremium }) => {
  const [open, setOpen] = useState(null);
  if (!summary) return <p className="text-white/50">Cargando...</p>;
  if (!summary.books?.length) return <p className="text-white/50" data-testid="editorial-no-sales">Aún no hay obras registradas.</p>;
  return (
    <div className="space-y-2" data-testid="editorial-sales-summary">
      {summary.books.map((b) => (
        <Card key={b.work_id} className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="gotham-medium text-white">{b.title}</div>
                <div className="text-white/45 text-xs">{b.author_name || 'Sin autor'} · ISBN {b.isbn}</div>
              </div>
              <div className="text-right">
                <div className="gotham-bold text-emerald-400">{money(b.gross_income)}</div>
                <div className="text-white/45 text-xs">{b.units_sold} uds.</div>
              </div>
            </div>
            {isPremium && b.stores && (
              <div className="mt-2">
                <button onClick={() => setOpen(open === b.work_id ? null : b.work_id)}
                  className="text-amber-400 text-xs flex items-center gap-1 hover:text-amber-300">
                  <Store className="w-3.5 h-3.5" /> {open === b.work_id ? 'Ocultar' : 'Ver'} desglose por tienda ({b.stores.length})
                </button>
                {open === b.work_id && (
                  <div className="mt-2 space-y-1">
                    {b.stores.length === 0 && <div className="text-white/40 text-xs">Sin ventas por tienda todavía.</div>}
                    {b.stores.map((s, i) => (
                      <div key={i} className="flex justify-between text-xs bg-black/30 rounded px-3 py-1.5">
                        <span className="text-white/70">{s.store}</span>
                        <span className="text-white/70">{s.units} uds · {money(s.gross)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const WorksList = ({ works, isPremium, headers, authors, onChange }) => {
  const [editing, setEditing] = useState(null);
  const [pct, setPct] = useState('');
  const [linked, setLinked] = useState('');
  const [uploadingCover, setUploadingCover] = useState(null);

  if (!works.length) return <p className="text-white/50" data-testid="editorial-no-works">Aún no has registrado obras. Ve a "Subir Metadata".</p>;

  const saveSplit = async (workId) => {
    try {
      await axios.patch(`${API}/editorial/works/${workId}/split`,
        { author_split_percent: parseFloat(pct) || 0, linked_author_id: linked || null }, { headers });
      toast.success('Split guardado');
      setEditing(null); setPct(''); setLinked('');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo guardar el split');
    }
  };

  const uploadCover = async (workId, f) => {
    if (!f) return;
    setUploadingCover(workId);
    try {
      const form = new FormData();
      form.append('file', f);
      await axios.post(`${API}/editorial/works/${workId}/cover`, form, { headers });
      toast.success('Portada actualizada');
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la portada');
    } finally {
      setUploadingCover(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="editorial-works-list">
      {works.map((w) => (
        <Card key={w.id} className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                {/* Portada + subida */}
                <label className="relative w-14 h-[74px] rounded-lg overflow-hidden border border-white/10 bg-black/40 flex items-center justify-center cursor-pointer group shrink-0"
                  data-testid={`editorial-cover-label-${w.id}`} title="Subir/cambiar portada">
                  {w.cover_url
                    ? <img src={w.cover_url} alt={w.title} className="w-full h-full object-cover" data-testid={`editorial-cover-thumb-${w.id}`} />
                    : <ImageIcon className="w-5 h-5 text-white/30" />}
                  <span className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-[10px] text-white text-center px-1">
                    {uploadingCover === w.id ? 'Subiendo...' : (w.cover_url ? 'Cambiar' : 'Subir portada')}
                  </span>
                  <input type="file" accept="image/*" className="hidden"
                    data-testid={`editorial-cover-file-${w.id}`}
                    disabled={uploadingCover === w.id}
                    onChange={(e) => uploadCover(w.id, e.target.files?.[0])} />
                </label>
                <div className="min-w-0">
                  <div className="gotham-medium text-white">{w.title}</div>
                  <div className="text-white/45 text-xs">{w.author_name || 'Sin autor'} · ISBN {w.isbn}</div>
                  {w.synopsis && <div className="text-white/40 text-xs mt-1 line-clamp-2 max-w-xl">{w.synopsis}</div>}
                </div>
              </div>
              {isPremium && (
                <div className="text-right shrink-0">
                  <div className="text-white/50 text-xs mb-1 flex items-center gap-1 justify-end">
                    <Percent className="w-3 h-3" /> Split autor: <strong className="text-amber-400">{w.author_split_percent || 0}%</strong>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`editorial-split-btn-${w.id}`}
                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-7 text-xs"
                    onClick={() => { setEditing(editing === w.id ? null : w.id); setPct(String(w.author_split_percent || '')); setLinked(w.linked_author_id || ''); }}>
                    Asignar split
                  </Button>
                </div>
              )}
            </div>
            {isPremium && editing === w.id && (
              <div className="mt-3 flex flex-wrap items-end gap-2 bg-black/30 rounded-lg p-3">
                <div>
                  <label className="text-white/50 text-xs block mb-1">% para el autor</label>
                  <input type="number" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)}
                    data-testid={`editorial-split-pct-${w.id}`}
                    className="w-24 px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 text-white text-sm" />
                </div>
                <div>
                  <label className="text-white/50 text-xs block mb-1">Autor vinculado</label>
                  <select value={linked} onChange={(e) => setLinked(e.target.value)}
                    data-testid={`editorial-split-author-${w.id}`}
                    className="px-3 py-1.5 rounded-lg bg-black/50 border border-white/10 text-white text-sm">
                    <option value="">— Ninguno —</option>
                    {authors.map((a) => <option key={a.id} value={a.id}>{a.full_name} ({a.email})</option>)}
                  </select>
                </div>
                <Button size="sm" onClick={() => saveSplit(w.id)} className="bg-amber-600 hover:bg-amber-500 text-white h-9">
                  Guardar
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const AuthorsList = ({ authors, headers, onChange }) => {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', username: '' });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState('');

  const create = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/editorial/authors`, form, { headers });
      toast.success('Cuenta Autor-Lite creada. Comparte la contraseña con tu autor.');
      setShow(false);
      setForm({ email: '', password: '', full_name: '', username: '' });
      onChange();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo crear la cuenta');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="editorial-authors-list">
      <div className="flex justify-between items-center">
        <p className="text-white/60 text-sm">Crea cuentas Autor-Lite para tus autores. Quedan como tus afiliados.</p>
        <Button data-testid="editorial-add-author-btn" onClick={() => setShow(!show)} className="bg-amber-600 hover:bg-amber-500 text-white">
          <Plus className="w-4 h-4 mr-2" /> Nuevo autor
        </Button>
      </div>

      {show && (
        <Card className="bg-white/[0.03] border-amber-500/20" data-testid="editorial-new-author-form">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: 'full_name', ph: 'Nombre completo' },
              { k: 'username', ph: 'Usuario' },
              { k: 'email', ph: 'Correo', type: 'email' },
              { k: 'password', ph: 'Contraseña (compártela con el autor)' },
            ].map((f) => (
              <input key={f.k} type={f.type || 'text'} placeholder={f.ph}
                value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                data-testid={`editorial-new-author-${f.k}`}
                className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 text-white text-sm" />
            ))}
            <div className="sm:col-span-2">
              <Button data-testid="editorial-save-author-btn" onClick={create} disabled={saving}
                className="bg-amber-600 hover:bg-amber-500 text-white">
                {saving ? 'Creando...' : 'Crear cuenta Autor-Lite'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!authors.length ? (
        <p className="text-white/50">Aún no has creado autores.</p>
      ) : authors.map((a) => (
        <Card key={a.id} className="bg-white/[0.03] border-white/10">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div>
              <div className="gotham-medium text-white">{a.full_name}</div>
              <div className="text-white/45 text-xs">{a.email} · @{a.username}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-1 rounded-full bg-white/10 text-white/70">Autor {a.author_tier}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(a.referral_code); setCopied(a.id); setTimeout(() => setCopied(''), 1500); }}
                className="text-white/50 hover:text-white text-xs flex items-center gap-1">
                {copied === a.id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                {a.referral_code}
              </button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default EditorialDashboard;
